import {
  PointerEvent as ReactPointerEvent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

export interface ZoomPan {
  scale: number;
  x: number;
  y: number;
}

export const IDENTITY: ZoomPan = { scale: 1, x: 0, y: 0 };
const MIN_SCALE = 1;
const MAX_SCALE = 12;

interface Options {
  /** Wheel/pinch-to-zoom is applied relative to this element's box. */
  viewportRef: RefObject<HTMLElement | null>;
}

/**
 * Zoom and pan a full-viewport element with the usual controls: mouse wheel and
 * trackpad pinch (a wheel event with ctrlKey) zoom around the cursor, touch pinch
 * zooms around the fingers, dragging pans, and a double tap/click resets. The state
 * lives here, not in the content, so swapping the content underneath keeps the view.
 *
 * The content is expected to have `transform-origin: 0 0` and to be the same size as
 * the viewport; the returned `transform` is `translate(x, y) scale(scale)`.
 */
export function useZoomPan({ viewportRef }: Options) {
  const [view, setView] = useState<ZoomPan>(IDENTITY);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const lastPinch = useRef<{ distance: number; midX: number; midY: number } | null>(null);
  const dragged = useRef(false);

  const zoomAt = useCallback(
    (px: number, py: number, factor: number) => {
      setView((current) => {
        const scale = clamp(current.scale * factor, MIN_SCALE, MAX_SCALE);
        if (scale === current.scale) return current;
        const ratio = scale / current.scale;
        // Keep the content point under (px, py) fixed while the scale changes.
        const next = { scale, x: px - (px - current.x) * ratio, y: py - (py - current.y) * ratio };
        return constrain(next, viewportRef.current);
      });
    },
    [viewportRef],
  );

  const panBy = useCallback(
    (dx: number, dy: number) => {
      setView((current) =>
        constrain({ ...current, x: current.x + dx, y: current.y + dy }, viewportRef.current),
      );
    },
    [viewportRef],
  );

  const reset = useCallback(() => setView(IDENTITY), []);

  // Wheel has to be registered non-passive to stop the browser zooming the page
  // itself on trackpad pinch, so it can't be a React prop.
  useEffect(() => {
    const element = viewportRef.current;
    if (element === null) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = element.getBoundingClientRect();
      // Trackpad pinch arrives as ctrl+wheel with small deltas; a mouse wheel notch is ~100.
      const sensitivity = event.ctrlKey ? 0.01 : 0.002;
      const factor = Math.exp(-event.deltaY * sensitivity);
      zoomAt(event.clientX - rect.left, event.clientY - rect.top, factor);
    };
    element.addEventListener("wheel", onWheel, { passive: false });
    return () => element.removeEventListener("wheel", onWheel);
  }, [viewportRef, zoomAt]);

  const local = (event: ReactPointerEvent) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    return { x: event.clientX - (rect?.left ?? 0), y: event.clientY - (rect?.top ?? 0) };
  };

  const onPointerDown = (event: ReactPointerEvent) => {
    if (event.button !== 0 && event.pointerType === "mouse") return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pointers.current.set(event.pointerId, local(event));
    lastPinch.current = pinchOf(pointers.current);
    dragged.current = false;
  };

  const onPointerMove = (event: ReactPointerEvent) => {
    const previous = pointers.current.get(event.pointerId);
    if (previous === undefined) return;
    const point = local(event);
    pointers.current.set(event.pointerId, point);

    const pinch = pinchOf(pointers.current);
    if (pinch !== null && lastPinch.current !== null) {
      const last = lastPinch.current;
      zoomAt(last.midX, last.midY, pinch.distance / last.distance);
      panBy(pinch.midX - last.midX, pinch.midY - last.midY);
      lastPinch.current = pinch;
      dragged.current = true;
      return;
    }
    lastPinch.current = pinch;
    if (pointers.current.size === 1) {
      const dx = point.x - previous.x;
      const dy = point.y - previous.y;
      if (Math.abs(dx) + Math.abs(dy) > 0) dragged.current = true;
      panBy(dx, dy);
    }
  };

  const onPointerUp = (event: ReactPointerEvent) => {
    pointers.current.delete(event.pointerId);
    lastPinch.current = pinchOf(pointers.current);
  };

  const onDoubleClick = () => {
    if (!dragged.current) reset();
  };

  return {
    view,
    zoomed: view.scale !== 1,
    transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onDoubleClick,
    },
  };
}

function pinchOf(pointers: Map<number, { x: number; y: number }>) {
  if (pointers.size < 2) return null;
  const [a, b] = [...pointers.values()];
  return {
    distance: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)),
    midX: (a.x + b.x) / 2,
    midY: (a.y + b.y) / 2,
  };
}

/** Keep the scaled content covering the viewport, and snap back to identity at 1x. */
function constrain(view: ZoomPan, viewport: HTMLElement | null): ZoomPan {
  if (view.scale <= MIN_SCALE) return IDENTITY;
  if (viewport === null) return view;
  const width = viewport.clientWidth;
  const height = viewport.clientHeight;
  return {
    scale: view.scale,
    x: clamp(view.x, width - width * view.scale, 0),
    y: clamp(view.y, height - height * view.scale, 0),
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
